import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  FaaReport,
  FaaReportWithJobs,
  FaaReportFilters,
  FaaReportOptions,
  QueueStats
} from '../models/faa-report.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

interface PaginatedApiResponse {
  success: boolean;
  data?: FaaReport[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  error?: string;
}

interface PaginatedReportsResponse {
  reports: FaaReport[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Batch generation request payload
 */
interface BatchGenerationRequest {
  organizationIds: string[];
  startDate: string;
  endDate: string;
  options: FaaReportOptions;
}

/**
 * Batch generation response
 */
export interface BatchGenerationResponse {
  created: number;
  reports: FaaReport[];
  errors: { organizationId: string; error: string }[];
}

/**
 * Aggregate pre-generation warning across the selected organizations
 * (FAA §F.2.d): records with no flight count entered, which will be filed as 1.
 *
 * `organizationsChecked` may be lower than the number requested — a per-org
 * failure is skipped rather than failing the whole check, so the warning is
 * a floor, not a guarantee.
 */
export interface BatchMissingCountSummary {
  organizationsChecked: number;
  totalRecords: number;
  recordsMissingCount: number;
  flightsMissingCount: number;
  organizations: {
    organizationId: string;
    recordsMissingCount: number;
    totalRecords: number;
  }[];
}

/**
 * FAA Report Service (Admin)
 *
 * Service for managing FAA reports in the admin portal.
 * Communicates with /admin/faa-reports endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class FaaReportService {
  private readonly apiUrl = `${environment.apiUrl}/admin/faa-reports`;

  constructor(private http: HttpClient) {}

  /**
   * List FAA reports with optional filters
   */
  async listReports(filters?: FaaReportFilters): Promise<PaginatedReportsResponse> {
    const params = new URLSearchParams();

    if (filters?.organizationId) {
      params.append('organizationId', filters.organizationId);
    }
    if (filters?.status) {
      params.append('status', filters.status);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }
    if (filters?.search) {
      params.append('search', filters.search);
    }
    if (filters?.page) {
      params.append('page', filters.page.toString());
    }
    if (filters?.pageSize) {
      params.append('limit', filters.pageSize.toString());
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}?${queryString}` : this.apiUrl;

    const response = await firstValueFrom(
      this.http.get<PaginatedApiResponse>(url)
    );

    if (response.success && response.data) {
      // Backend returns data as array directly with total/page/limit at root level
      return {
        reports: response.data,
        total: response.total ?? 0,
        page: response.page ?? 1,
        pageSize: response.limit ?? 20
      };
    }

    return { reports: [], total: 0, page: 1, pageSize: 20 };
  }

  /**
   * Get a single report with job information
   */
  async getReport(id: string): Promise<FaaReportWithJobs | null> {
    // Backend returns { success, data: { report, jobs } }
    interface ReportApiResponse {
      success: boolean;
      data?: {
        report: FaaReport;
        jobs: FaaReportWithJobs['jobs'];
      };
      error?: string;
    }

    const response = await firstValueFrom(
      this.http.get<ReportApiResponse>(`${this.apiUrl}/${id}`)
    );

    if (response.success && response.data) {
      // Merge report fields with jobs array
      return {
        ...response.data.report,
        jobs: response.data.jobs || []
      };
    }

    return null;
  }

  /**
   * Delete a report (soft delete)
   */
  async deleteReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`)
    );

    return response.success;
  }

  /**
   * Update report dates
   */
  async updateReportDates(id: string, startDate: string, endDate: string): Promise<FaaReport | null> {
    const response = await firstValueFrom(
      this.http.put<ApiResponse<FaaReport>>(`${this.apiUrl}/${id}`, { startDate, endDate })
    );

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error(response.error || 'Failed to update report dates');
  }

  /**
   * Regenerate a report (create new generation job)
   */
  async regenerateReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/regenerate`, {})
    );

    return response.success;
  }

  /**
   * Retry a failed report generation job
   */
  async retryReport(id: string): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.post<ApiResponse<void>>(`${this.apiUrl}/${id}/retry`, {})
    );

    return response.success;
  }

  /**
   * Get queue statistics
   */
  async getQueueStats(): Promise<QueueStats> {
    const response = await firstValueFrom(
      this.http.get<ApiResponse<QueueStats>>(`${this.apiUrl}/queue-stats`)
    );

    if (response.success && response.data) {
      return response.data;
    }

    return { pending: 0, active: 0, completed: 0, failed: 0 };
  }

  /**
   * Aggregate missing-flight-count warning across the selected organizations
   * (FAA §F.2.d), for the batch-generation confirm modal.
   *
   * Advisory only. Returns null on any failure so a check that cannot be
   * computed never stops an operator filing — an under-count is a compliance
   * problem, being unable to file at all is worse.
   */
  async fetchMissingFlightCounts(
    organizationIds: string[],
    startDate: string,
    endDate: string
  ): Promise<BatchMissingCountSummary | null> {
    try {
      const response = await firstValueFrom(
        this.http.post<ApiResponse<BatchMissingCountSummary>>(
          `${this.apiUrl}/missing-flight-counts`,
          { organizationIds, startDate, endDate }
        )
      );

      return response?.success && response.data ? response.data : null;
    } catch (error) {
      console.error('Error fetching missing flight counts:', error);
      return null;
    }
  }

  /**
   * Generate batch FAA reports for multiple organizations
   *
   * Creates FAA report records and queues generation jobs for each selected organization.
   *
   * @param organizationIds - Array of organization IDs to generate reports for
   * @param startDate - Report start date (YYYY-MM-DD format)
   * @param endDate - Report end date (YYYY-MM-DD format)
   * @param options - FAA report options (include flight logs, spray records, etc.)
   * @returns BatchGenerationResponse with created count, reports, and any errors
   */
  async generateBatch(
    organizationIds: string[],
    startDate: string,
    endDate: string,
    options: FaaReportOptions
  ): Promise<BatchGenerationResponse> {
    const requestBody: BatchGenerationRequest = {
      organizationIds,
      startDate,
      endDate,
      options
    };

    const response = await firstValueFrom(
      this.http.post<ApiResponse<BatchGenerationResponse>>(`${this.apiUrl}/batch`, requestBody)
    );

    if (response.success && response.data) {
      return response.data;
    }

    // Return empty response if something went wrong
    return {
      created: 0,
      reports: [],
      errors: []
    };
  }
}
